import React from 'react';
import styled from 'styled-components';
import { ReactComponent as FailureIcon } from '@elementary/common-ui/assets/close.svg';
import { ReactComponent as InfoIcon } from '@elementary/common-ui/assets/16x16/info.svg';
import { ReactComponent as MoreHorizIcon } from '@elementary/common-ui/assets/more.svg';
import { ReactComponent as SyncArrowsIcon } from '@elementary/common-ui/assets/sync-arrows.svg';
import { ReactComponent as VIcon } from '@elementary/common-ui/assets/v-green.svg';
import { ReactComponent as WarningIcon } from '@elementary/common-ui/assets/warning.svg';
import {
  Flex,
  FlexCenter,
  FlexColumn,
  FlexSpace,
  IconButton,
  Menu,
  SvgIcon,
} from '@elementary/common-ui/components';
import themeBase from '@elementary/common-ui/style/theme';
import { capitalize } from '@elementary/common-ui/utils/common';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import { Tooltip, Typography } from '@mui/material';
import { IntegrationIssue } from 'shared/types';
import {
  fontSize,
  fontWeight,
  radius,
  space,
} from '@elementary/common-ui/style/utils';

const STATUS = {
  CONNECTED: 'connected',
  SYNCING: 'syncing',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
};

const STATUS_SEVERITY_ORDER: Record<string, number> = {
  [STATUS.ERROR]: 3,
  [STATUS.WARNING]: 2,
  [STATUS.INFO]: 1,
  [STATUS.SYNCING]: 0,
  [STATUS.CONNECTED]: 0,
};

const STATUS_LABELS: Record<string, string> = {
  [STATUS.INFO]: 'Re-authentication needed',
};

const FailureIconStyled = styled(FailureIcon)`
  * {
    stroke: ${({ theme }) => theme.palette.status.error.main};
  }
`;

const statusStylesMap = {
  [STATUS.CONNECTED]: {
    background: themeBase.palette.status.success.bg,
    text: themeBase.palette.status.success.main,
    icon: <VIcon width={18} />,
  },
  [STATUS.SYNCING]: {
    background: themeBase.palette.surface.active,
    text: themeBase.palette.accent.secondary,
    icon: (
      <SvgIcon width={16} fillColor="accent.secondary">
        <SyncArrowsIcon />
      </SvgIcon>
    ),
  },
  [STATUS.INFO]: {
    background: themeBase.palette.surface.active,
    text: themeBase.palette.accent.secondary,
    icon: (
      <SvgIcon width={16} fillColor="accent.secondary">
        <InfoIcon />
      </SvgIcon>
    ),
  },
  [STATUS.WARNING]: {
    background: themeBase.palette.status.warning.bg,
    text: themeBase.palette.status.warning.strong,
    icon: (
      <SvgIcon width={16}>
        <WarningIcon />
      </SvgIcon>
    ),
  },
  [STATUS.ERROR]: {
    background: themeBase.palette.status.error.bg,
    text: themeBase.palette.status.error.main,
    icon: <FailureIconStyled width={18} />,
  },
};

const ItemWrapper = styled(FlexSpace)`
  gap: ${space('sm')};
  align-items: center;
  border: 1px solid ${({ theme }) => theme.palette.border.default};
  padding: ${space('lg')};
  border-radius: ${radius('lg')};
  box-sizing: border-box;
  height: 56px;
`;

const StatusChip = styled(Flex)<{ status: keyof typeof statusStylesMap }>`
  font-size: ${fontSize('base')};
  padding: ${space('xs', 'sm')};
  gap: ${space('xs')};
  border-radius: ${radius('sm')};
  white-space: nowrap;
  background-color: ${({ status }) => statusStylesMap[status].background};
  color: ${({ status }) => statusStylesMap[status].text};
  font-weight: ${fontWeight('medium')};
  width: fit-content;
  align-items: center;
`;

export type IntegrationDetails = {
  id: string;
  icon: React.ReactNode;
  name: string;
  status?: keyof typeof statusStylesMap;
  statusTooltip?: string | null;
  last_synced_at?: string | null;
  usageText?: string;
  customOptions?: { value: string; label: string; color?: string }[];
  issues?: IntegrationIssue[];
};

export type MultipleDestinationsRendererProps = {
  integrations: IntegrationDetails[];
  onChange: (method: string, id: string) => void;
  options: { value: string; label: string; color?: string }[];
};

function computeEffectiveStatus(
  status: keyof typeof statusStylesMap | undefined,
  issues: IntegrationIssue[] | undefined,
): keyof typeof statusStylesMap | undefined {
  const worstIssueSeverity = (issues ?? []).reduce<string | undefined>(
    (worst, issue) => {
      if (
        worst === undefined ||
        (STATUS_SEVERITY_ORDER[issue.severity] ?? 0) >
          (STATUS_SEVERITY_ORDER[worst] ?? 0)
      ) {
        return issue.severity;
      }
      return worst;
    },
    undefined,
  );

  if (worstIssueSeverity === undefined) return status;
  if (status === undefined)
    return worstIssueSeverity as keyof typeof statusStylesMap;

  return (STATUS_SEVERITY_ORDER[worstIssueSeverity] ?? 0) >
    (STATUS_SEVERITY_ORDER[status] ?? 0)
    ? (worstIssueSeverity as keyof typeof statusStylesMap)
    : status;
}

function computeCombinedTooltip(
  statusTooltip: string | null | undefined,
  issues: IntegrationIssue[] | undefined,
): string | null {
  const parts = [
    ...(statusTooltip ? [statusTooltip] : []),
    ...(issues ?? []).map((issue) => issue.description),
  ];
  return parts.length > 0 ? parts.join('\n') : null;
}

const multipleDestinationsRenderer = ({
  integrations,
  onChange,
  options,
}: MultipleDestinationsRendererProps): React.ReactNode => (
  <FlexColumn sx={{ gap: space('lg') }}>
    {integrations.map((integration) => {
      const lastSyncedLabel = integration.last_synced_at
        ? `Synced ${localizeDate(integration.last_synced_at).fromNow()}`
        : null;

      const effectiveStatus = computeEffectiveStatus(
        integration.status,
        integration.issues,
      );
      const combinedTooltip = computeCombinedTooltip(
        integration.statusTooltip,
        integration.issues,
      );
      const statusLabel = effectiveStatus
        ? STATUS_LABELS[effectiveStatus] ??
          capitalize(effectiveStatus as string)
        : null;

      return (
        <ItemWrapper key={integration.id}>
          <FlexCenter sx={{ gap: space('sm') }}>
            {integration.icon}
            <Typography fontSize="14px">{integration.name}</Typography>
            {integration.usageText && (
              <Typography
                fontSize="12px"
                sx={{ color: 'content.secondary', marginLeft: space('sm') }}
              >
                {integration.usageText}
              </Typography>
            )}
          </FlexCenter>

          <Flex sx={{ gap: space('md'), alignItems: 'center' }}>
            <Flex sx={{ gap: space('sm'), alignItems: 'center' }}>
              {lastSyncedLabel && (
                <Typography
                  noWrap
                  sx={{
                    fontSize: fontSize('sm'),
                    color: 'content.secondary',
                  }}
                >
                  {lastSyncedLabel}
                </Typography>
              )}
              {effectiveStatus &&
                (combinedTooltip ? (
                  <Tooltip title={combinedTooltip} placement="top" arrow>
                    <span tabIndex={0} style={{ display: 'inline-flex' }}>
                      <StatusChip status={effectiveStatus}>
                        {statusStylesMap[effectiveStatus].icon}
                        {statusLabel}
                      </StatusChip>
                    </span>
                  </Tooltip>
                ) : (
                  <StatusChip status={effectiveStatus}>
                    {statusStylesMap[effectiveStatus].icon}
                    {statusLabel}
                  </StatusChip>
                ))}
            </Flex>

            <Menu
              sx={{ cursor: 'pointer', marginLeft: space('lg') }}
              closeOnChange
              menuButton={
                <IconButton>
                  <FlexCenter sx={{ height: '14px', width: '14px' }}>
                    <MoreHorizIcon />
                  </FlexCenter>
                </IconButton>
              }
              options={integration.customOptions || options}
              onChange={(value) => onChange(value, integration.id)}
            />
          </Flex>
        </ItemWrapper>
      );
    })}
  </FlexColumn>
);

export default multipleDestinationsRenderer;
